import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import { publicApi } from "../../api/http";
import { Link } from "react-router-dom";
import PlayerNhlStatsGraph from "./PlayerNhlStatsGraph";
import PlayerStatsTable from "./PlayerStatsTable";

function parseNhlStatsResponse(data) {
    if (typeof data === "string") {
        try {
            return JSON.parse(data);
        } catch {
            return [];
        }
    }
    return Array.isArray(data) ? data : [];
}

function roundNhle(value) {
    if (value == null || Number.isNaN(Number(value))) return null;
    return Math.round(Number(value) * 10) / 10;
}

function applyNhleBasis(row) {
    const factor =
        row.nhle_factor == null || Number.isNaN(Number(row.nhle_factor))
            ? null
            : Number(row.nhle_factor);

    if (factor == null) {
        return {
            ...row,
            goals: null,
            assists: null,
            points: null,
            nhle_applied: false,
            nhle_factor: null,
        };
    }

    const rawGoals = row.goals == null ? null : Number(row.goals);
    const rawAssists = row.assists == null ? null : Number(row.assists);
    const rawPoints =
        row.points == null
            ? rawGoals == null && rawAssists == null
                ? null
                : (rawGoals || 0) + (rawAssists || 0)
            : Number(row.points);

    return {
        ...row,
        goals: rawGoals == null ? null : roundNhle(rawGoals * factor),
        assists: rawAssists == null ? null : roundNhle(rawAssists * factor),
        points: rawPoints == null ? null : roundNhle(rawPoints * factor),
        nhle_applied: true,
        nhle_factor: factor,
    };
}

function PlayerNhlCareerStatsTab({
    playerId,
    playerData,
    isActive,
    initialDisplay = "table",
    initialBasis = "raw",
    initialGraphPrefs = null,
    onShareStateChange,
}) {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [displayMode, setDisplayMode] = useState(initialDisplay === "graph" ? "graph" : "table");
    const [statBasis, setStatBasis] = useState(initialBasis === "nhle" ? "nhle" : "raw");
    const [graphPrefs, setGraphPrefs] = useState(initialGraphPrefs);

    useEffect(() => {
        if (!playerId || !isActive) {
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        publicApi
            .get(`/player_nhl_stats/${playerId}`)
            .then((response) => {
                if (cancelled) return;
                setStats(parseNhlStatsResponse(response.data));
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Error fetching NHL career stats:", err);
                setError("Failed to load NHL career stats.");
                setStats([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [playerId, isActive]);

    const isGoalie = useMemo(() => {
        if (playerData?.position === "G") {
            return true;
        }
        if (playerData?.position) {
            return false;
        }
        return stats.some(
            (row) => parseFloat(row.save_percentage) > 0 || parseFloat(row.wins) > 0
        );
    }, [playerData, stats]);

    const showNhle = !isGoalie && statBasis === "nhle";

    const displayStats = useMemo(() => {
        if (!showNhle) return stats;
        return stats.map(applyNhleBasis);
    }, [stats, showNhle]);

    useEffect(() => {
        onShareStateChange?.({
            displayMode,
            statBasis,
            graphPrefs,
        });
    }, [displayMode, statBasis, graphPrefs, onShareStateChange]);

    if (loading) {
        return (
            <div className="player-nhl-stats-loading">
                <CircularProgress size={28} />
            </div>
        );
    }

    if (error) {
        return (
            <Typography variant="body1" component="div" className="empty-tab-message">
                {error}
            </Typography>
        );
    }

    return (
        <div className="nhl-stats-tab">
            <div className="nhl-stats-header">
                <div>
                    <Typography variant="h4" component="div" className="tab-section-title">
                        NHL Career Stats
                    </Typography>
                    <Typography variant="body2" component="div" className="tab-section-subtitle">
                        {showNhle ? (
                            <>
                                G/A/PTS/PPG are NHL-equivalent. Fac is the league NHLe factor.{" "}
                                <Link to="/how-projections-work#nhle">How this works</Link>
                            </>
                        ) : (
                            "Career totals synced from the NHL API across all leagues."
                        )}
                    </Typography>
                </div>
                {stats.length > 0 && (
                    <div className="nhl-stats-header-controls">
                        {!isGoalie && (
                            <ToggleButtonGroup
                                value={statBasis}
                                exclusive
                                size="small"
                                onChange={(_, value) => value && setStatBasis(value)}
                                className="nhl-stats-display-toggle"
                                aria-label="Stat basis"
                            >
                                <ToggleButton value="raw">Raw</ToggleButton>
                                <ToggleButton value="nhle">NHL-E</ToggleButton>
                            </ToggleButtonGroup>
                        )}
                        <ToggleButtonGroup
                            value={displayMode}
                            exclusive
                            size="small"
                            onChange={(_, value) => value && setDisplayMode(value)}
                            className="nhl-stats-display-toggle"
                        >
                            <ToggleButton value="table">
                                <TableChartOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
                                Table
                            </ToggleButton>
                            <ToggleButton value="graph">
                                <ShowChartOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
                                Graph
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </div>
                )}
            </div>

            {stats.length > 0 ? (
                displayMode === "graph" ? (
                    <PlayerNhlStatsGraph
                        stats={displayStats}
                        isGoalie={isGoalie}
                        playerId={playerId}
                        initialPrefs={initialGraphPrefs}
                        onPrefsChange={setGraphPrefs}
                    />
                ) : (
                    <PlayerStatsTable
                        rows={displayStats}
                        isGoalie={isGoalie}
                        showNhle={showNhle}
                        showPlusMinus={!isGoalie}
                    />
                )
            ) : (
                <Typography variant="body1" component="div" className="no-stats-message">
                    No NHL career stats synced for this player yet.
                </Typography>
            )}
        </div>
    );
}

export default PlayerNhlCareerStatsTab;
